import {
  createNamedErrorType,
  LassoErrorCode,
  lassoExceptionSchema,
} from "@/types/lasso-exception";
import { api } from "@/utils/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { HTTPError } from "ky";

export const PaymentRequiredError = createNamedErrorType({
  paymentRequired: () =>
    "Du har ikke nok credits til at købe dette telefonnummer.",
});

export interface PhoneNumbersResponse {
  lassoId: string;
  isRobinson: boolean;
  isCommerciallyProtected: boolean;
  numbers: PhoneNumber[];
}

// Note: The "Number" interface is named as such to align with
// the API response, even though it may be confused with the built-in Number type in ts.
// So i renamed the interface to "PhoneNumber" to avoid confusion and improve code clarity.
export interface PhoneNumber {
  callable: boolean;
  phoneNumber: string;
  sources: Source[];
  score: number;
  explanation: string;
  obfuscated?: boolean;
}

export interface Source {
  links: string[] | null;
  type: Type;
  timestamp: string;
  note: string | null;
}

export enum Type {
  CVR = "CVR",
  Teledata = "Teledata",
  Website = "Website",
  Google = "Google",
}

/**
 * Custom hook to fetch the user's phone number
 */
export const usePhoneNumber = (
  leadId: string,
  lassoId: string,
  purchase = false,
) => {
  return useQuery<PhoneNumbersResponse>({
    queryKey: ["phoneNumber", leadId, lassoId],
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      queryParams.append("lassoid", lassoId);
      queryParams.append("purchase", purchase ? "true" : "false");
      return await api
        .get(`apps/sales/leads/${leadId}/livenumbers?${queryParams.toString()}`)
        .then((res) => res.json<PhoneNumbersResponse>());
    },
    enabled: !!leadId && !!lassoId,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });
};

export const usePurchaseLiveNumbers = (leadId: string, lassoId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const queryParams = new URLSearchParams();
      queryParams.append("lassoid", lassoId);
      queryParams.append("purchase", "true");
      try {
        const response = await api
          .get(
            `apps/sales/leads/${leadId}/livenumbers?${queryParams.toString()}`,
          )
          .json<PhoneNumbersResponse>();
        return response;
      } catch (error) {
        if (error instanceof HTTPError) {
          const json = await error.response.json().catch(() => null);
          const { data: lassoException } = lassoExceptionSchema.safeParse(json);

          if (lassoException) {
            switch (lassoException.errorCode) {
              case LassoErrorCode.LimitReachedError:
                throw new PaymentRequiredError("paymentRequired");

              default:
                throw new Error(
                  `Unhandled error code: ${lassoException.errorCode}, message: ${lassoException.errorMessage}`,
                );
            }
          }
        }
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["phoneNumber", leadId, lassoId],
      });
    },
  });
};
